<script setup lang="ts">
import { computed } from 'vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import BaseTag from '@/components/ui/tag/BaseTag.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import type { PageContent } from '@/assets/ts/pages/shipment-protection-shipping'
import buttonVariants from '@/components/ui/button'
import { isMobile } from '@/legacy/ts/checkDevice'

type ButtonVariant = NonNullable<
	Parameters<typeof buttonVariants>[0]
>['variant']

const props = defineProps<{
	pageContent: PageContent
	theme: 'shopping' | 'shipping'
}>()

const emit = defineEmits<{
	(e: 'scroll-to-top', theme: string): void
}>()

type ThemeStyle = {
	borderColor: string
	bgColor: string
	buttonColor: ButtonVariant
}

const themeStyles: Record<
	'shipping' | 'shopping',
	Record<string, ThemeStyle>
> = {
	shipping: {
		shippingBasic: {
			borderColor: 'border-n-75',
			bgColor: 'bg-n-75',
			buttonColor: 'secondary',
		},
		shippingPlus: {
			borderColor: 'border-n-950',
			bgColor: 'bg-o-100',
			buttonColor: 'primary',
		},
	},
	shopping: {
		shoppingBasic: {
			borderColor: 'border-n-950',
			bgColor: 'bg-t-100',
			buttonColor: 'pcs-primary',
		},
	},
} as const

const styledCards = computed(() => {
	const currentTheme = props.theme as keyof typeof themeStyles
	const themeMap = themeStyles[currentTheme] ?? {}

	return (
		props.pageContent?.planeCard?.map(item => {
			const itemTheme = item.theme as keyof typeof themeMap
			const style = themeMap?.[itemTheme] || {}

			return {
				...item,
				...(style as ThemeStyle),
				list: item.list.map(listItem => ({
					...listItem,
					iconFillColor:
						listItem.icon === 'checkmark' ? 'fill-g-600' : 'fill-n-950',
				})),
			}
		}) ?? []
	)
})

const handleButtonClick = (theme: string) => {
	emit('scroll-to-top', theme)
}
</script>

<template>
	<div class="bs-container py-acquaintances">
		<div class="col-span-12">
			<div class="flex flex-col gap-20px mdp:flex-row">
				<div
					v-for="card in styledCards"
					:key="card.theme"
					class="relative flex flex-1 flex-col gap-casual-friends overflow-hidden rounded-xl border border-[1.4px] bg-white p-casual-friends"
					:class="card.borderColor"
				>
					<div
						class="flex flex-col"
						:class="{
							'mdp:flex-row': theme === 'shopping',
							'gap-casual-friends': theme === 'shipping',
							'gap-acquaintances': theme === 'shopping',
						}"
					>
						<div
							class="absolute left-0 top-0 h-12px w-full rounded-t-xl"
							:class="card.bgColor"
						/>
						<!-- header -->
						<div
							class="flex w-full flex-col flex-wrap gap-good-friends"
							:class="{ 'mdp:flex-1': theme === 'shopping' }"
						>
							<div>
								<div class="flex flex-wrap items-center">
									<!-- title -->
									<div
										class="typography-h4-str mr-20px max-w-full break-words text-n-950"
										v-html="card.title"
									/>

									<div class="min-w-fit">
										<BaseTag
											v-if="card.hasTag"
											variant="dark"
											size="medium"
											class="typography-c1-reg break-all rounded-sm"
										>
											{{ card.tag }}
										</BaseTag>
									</div>
								</div>

								<div class="typography-s1-reg mt-8px min-h-[48px] text-n-800">
									{{ card.description }}
								</div>
							</div>

							<div class="typography-s1-emp flex flex-col gap-8px text-n-950">
								<div class="min-h-[24px]">{{ card.priceTitle }}</div>
								<div class="flex items-end gap-12px">
									<div class="typography-h2-str">{{ card.price }}</div>
									<div class="typography-s1-reg py-4px">
										{{ card.priceCaption }}
									</div>
								</div>
							</div>

							<div>
								<BaseButton
									:variant="card.buttonColor"
									:isFull="true"
									size="medium"
									@click="handleButtonClick(card.theme)"
								>
									{{ card.buttonLabel }}
								</BaseButton>
							</div>
						</div>

						<div
							class="flex flex-col gap-16px"
							:class="{ 'mdp:flex-1': theme === 'shopping' }"
						>
							<div
								v-if="card.listTitle && !isMobile"
								class="typography-h5-str text-n-950"
							>
								{{ card.listTitle }}
							</div>
							<div
								v-for="item in card.list"
								:key="item.label"
								class="flex items-center gap-16px"
							>
								<div v-if="item.label" class="min-w-fit">
									<BaseIcon
										:name="item.icon"
										class="h-20px w-20px"
										:class="item.iconFillColor"
									/>
								</div>
								<div class="typography-s1-reg" v-html="item.label" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
